// Whose turn it is on a decision the studio asked a client for, decided once.
//
// `approvals.status` had three writers and three vocabularies:
//
//   * `AdminApprovals` wrote `pending` on send and `approved` on the studio's
//     own tick.
//   * `Approvals` (the client's page) wrote `approved` and `revision-requested`.
//   * `AdminRevisions` wrote `resolved` — and alongside it `resolved_at`, a
//     column `approvals` does not have. PostgREST rejected the whole update,
//     the result was never destructured, and the card flipped to Resolved in
//     local state anyway. "Mark Resolved" had never once changed a row.
//
// Twelve other readers — both sidebars, both dashboards, the notification
// bell, the client's Projects page, client health, nudges, portal activity —
// all count `status = 'pending'` and nothing else. So `resolved` was a fourth
// state that only the screen that wrote it understood, and `revision-requested`
// was a dead end: it left `pending`, so it vanished from every badge and queue,
// and there was no path back into the client's hands. The client got asked
// once, answered once, and the loop ended there.
//
// So there are three stored values and no more. A revision the studio has
// finished goes back to `pending` — the client's turn again — which is the
// only word every reader already agrees on.

/** The only values that belong in `approvals.status`. */
export const APPROVAL_STATUSES = ['pending', 'revision-requested', 'approved']

/** Inside this many days of the review deadline the client needs a nudge. */
export const DUE_SOON_DAYS = 3

export const APPROVAL_STAGES = [
  { key: 'awaiting-client', label: 'Awaiting client', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  { key: 'due-soon', label: 'Due soon', color: '#C2410C', bg: '#FFF7ED', border: '#FED7AA' },
  { key: 'overdue', label: 'Overdue', color: '#BE123C', bg: '#FFF1F2', border: '#FECACA' },
  { key: 'awaiting-studio', label: 'Revision requested', color: '#E65100', bg: '#FFF3E0', border: '#FFCC80' },
  { key: 'approved', label: 'Approved', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
]

const STAGE_BY_KEY = Object.fromEntries(APPROVAL_STAGES.map(s => [s.key, s]))

const lower = (v) => String(v || '').trim().toLowerCase()

/**
 * The stored word, reduced to the three that mean something.
 *
 * `resolved` could never actually reach the column, because the update that
 * wrote it always failed. It is mapped rather than ignored so that a row
 * written by hand still lands somewhere sensible: the studio finished the
 * revision, so the decision is the client's again.
 */
export function normalizeStatus(status) {
  const s = lower(status)
  if (s === 'approved') return 'approved'
  if (s === 'revision-requested' || s === 'revision_requested') return 'revision-requested'
  return 'pending'
}

function daysUntil(dueDate, now) {
  if (!dueDate) return null
  // Dates are stored as `date`, so compare at day granularity — an approval
  // due today is not overdue until tomorrow.
  const due = new Date(`${String(dueDate).slice(0, 10)}T23:59:59.999Z`)
  if (Number.isNaN(due.getTime())) return null
  return Math.ceil((due.getTime() - now.getTime()) / 86400000)
}

/**
 * Where this decision stands.
 *
 * Overdue and due-soon are derived from `due_date`, never stored — the same
 * rule as `invoiceStage()`. Only an approval still waiting on the client can
 * be overdue; once they have answered, the deadline stops mattering.
 */
export function approvalStage(approval, now = new Date()) {
  const status = normalizeStatus(approval?.status)
  if (status === 'approved') return STAGE_BY_KEY['approved']
  if (status === 'revision-requested') return STAGE_BY_KEY['awaiting-studio']

  const days = daysUntil(approval?.due_date, now)
  if (days !== null && days < 0) return STAGE_BY_KEY['overdue']
  if (days !== null && days <= DUE_SOON_DAYS) return STAGE_BY_KEY['due-soon']
  return STAGE_BY_KEY['awaiting-client']
}

/** Still needs somebody to do something. */
export function isOpen(approval) {
  return normalizeStatus(approval?.status) !== 'approved'
}

/** The ball is in the client's court. */
export function awaitsClient(approval) {
  return normalizeStatus(approval?.status) === 'pending'
}

/** The ball is in the studio's court. */
export function awaitsStudio(approval) {
  return normalizeStatus(approval?.status) === 'revision-requested'
}

export function isOverdue(approval, now = new Date()) {
  return approvalStage(approval, now).key === 'overdue'
}

/**
 * What to call this on screen.
 *
 * An approval either hangs off a deliverable or carries its own title, and
 * which one depends on the screen that created it. The client's page read only
 * `deliverables.title`, so every approval sent from `/approvals` — where the
 * form has no deliverable at all — reached the client as a card with a blank
 * heading and a blank line in the revision picker.
 */
export function approvalTitle(approval) {
  const fromDeliverable = approval?.deliverables?.title
  return (fromDeliverable || approval?.title || '').trim() || 'Untitled deliverable'
}

/**
 * The revision allowance, or null when there is nothing to count against.
 *
 * The client's card rendered `revisions_used || 0` / `revisions_max || 2`,
 * which read "0/2 revisions" for every approval in the portal: nothing has
 * ever incremented `revisions_used`, and an approval with no deliverable has
 * no allowance in the first place. A number that is always the same number is
 * not information, and on a fixed-fee engagement it is the wrong one to invent.
 */
export function revisionAllowance(approval) {
  const d = approval?.deliverables
  if (!d || (d.revisions_max === null && d.revisions_used === null)) return null
  const max = Number.isFinite(d.revisions_max) ? d.revisions_max : 2
  const used = Number.isFinite(d.revisions_used) ? d.revisions_used : 0
  return { used, max, remaining: Math.max(0, max - used), exhausted: used >= max }
}

/** Counts for the header tiles, from one pass over the rows. */
export function summarizeApprovals(approvals, now = new Date()) {
  const list = approvals || []
  const summary = { total: list.length, awaitingClient: 0, overdue: 0, awaitingStudio: 0, approved: 0, open: 0 }
  for (const a of list) {
    const stage = approvalStage(a, now)
    if (stage.key === 'approved') summary.approved += 1
    else if (stage.key === 'awaiting-studio') summary.awaitingStudio += 1
    else {
      summary.awaitingClient += 1
      if (stage.key === 'overdue') summary.overdue += 1
    }
    if (isOpen(a)) summary.open += 1
  }
  return summary
}

/**
 * What an approval request actually writes, decided in one place.
 *
 * Three screens raise an approval — `/approvals`, the client drawer on
 * `/admin`, and `AdminClients` — and all three used to decide this for
 * themselves. Two of them got it wrong in the same way and for the same
 * reason: they always created a deliverable, and never gave it a
 * `project_id`. The client's Projects page filters deliverables strictly on
 * `project_id`, so those rows were visible to the admin who made them and to
 * nobody else. `Admin.js` was worse than a null — it filed the deliverable
 * against `projects[0]`, whichever project happened to sort first, which for
 * a client with thirteen of them is a guess presented as a fact.
 *
 * The rule: naming a project creates the deliverable, so the decision reaches
 * the work and has an allowance to count against. Leaving it blank creates a
 * standalone approval and no deliverable at all — a request that is honestly
 * unattached, rather than a row only one side can see.
 *
 * `project_id` also goes on the approval itself. 0032 added that column for
 * `AdminDashboard`'s `projects(title)` embed; without it the "Pending
 * Approvals" card resolves a null and names no project.
 *
 * Returns `{ deliverable, approval }`, where `deliverable` is null when no
 * project was chosen. The caller inserts the deliverable first, then puts its
 * id on the approval via `withDeliverableId`.
 */
export function approvalRequestWrites({
  clientId, projectId, title, description, dueDate, context, fileUrl, storagePath,
} = {}) {
  const trimmed = (title || '').trim()
  const proj = projectId || null
  const note = description ?? context ?? null

  const deliverable = proj
    ? {
        client_id: clientId,
        project_id: proj,
        title: trimmed,
        description: note || null,
        due_date: dueDate || null,
        status: 'pending',
      }
    : null

  const approval = {
    client_id: clientId,
    title: trimmed,
    due_date: dueDate || null,
    status: 'pending',
  }
  if (context) approval.context = context
  if (proj) approval.project_id = proj
  // Two different things: file_url is a link someone pasted (a Figma board),
  // storage_path is a file uploaded into the private approval-files bucket,
  // signed on read. A public URL for the latter would 404 now.
  if (fileUrl) approval.file_url = fileUrl
  if (storagePath) approval.storage_path = storagePath

  return { deliverable, approval }
}

/** Puts the inserted deliverable's id on the approval, or leaves it standalone. */
export function withDeliverableId(approval, deliverableId) {
  if (!deliverableId) return approval
  return { ...approval, deliverable_id: deliverableId }
}
